/**
 * Onde o botão de feedback mora na tela — e o painel, que abre colado nele.
 *
 * **A pessoa arrasta e ele fica.** O canto de baixo à direita cobria coisa em
 * algumas telas (o × das linhas de relés, a barra de ações de um formulário), e
 * cada um trabalha com um layout diferente. A posição é guardada neste navegador.
 *
 * **Guardada como distância da direita e de baixo**, e não de cima e da esquerda:
 * o canto de baixo à direita é onde ele nasce, e medir a partir dele faz o botão
 * acompanhar a janela quando ela muda de tamanho, em vez de ficar para trás.
 *
 * Toda posição passa por {@link prender}: um valor salvo numa tela grande não
 * pode deixar o botão fora de uma tela menor.
 */

export type Posicao = { direita: number; baixo: number }
export type Tamanho = { largura: number; altura: number }

/** 30 px da barra de estado + 20 do rodapé de versão + folga: não cobre o rodapé. */
export const POSICAO_PADRAO: Posicao = { direita: 20, baixo: 66 }

/** Quanto o botão e o painel ficam longe da borda, no mínimo. */
export const MARGEM = 8

/** Quanto o ponteiro precisa andar para virar arrasto — abaixo disso é clique. */
export const LIMIAR_DO_ARRASTO = 5

const CHAVE = "sistema-oc.feedback-posicao"

export function lerPosicao(): Posicao {
  try {
    const salva = JSON.parse(localStorage.getItem(CHAVE) ?? "null") as Partial<Posicao> | null
    if (salva && Number.isFinite(salva.direita) && Number.isFinite(salva.baixo)) {
      return { direita: salva.direita!, baixo: salva.baixo! }
    }
  } catch { /* sem armazenamento, ou valor estragado */ }
  return POSICAO_PADRAO
}

export function gravarPosicao(p: Posicao | null) {
  try {
    if (p) localStorage.setItem(CHAVE, JSON.stringify(p))
    else localStorage.removeItem(CHAVE)
  } catch { /* sem armazenamento: vale só até recarregar */ }
}

/** Mantém o botão inteiro dentro da janela. */
export function prender(p: Posicao, janela: Tamanho, botao: Tamanho): Posicao {
  const limitar = (v: number, max: number) => Math.round(Math.min(Math.max(v, MARGEM), Math.max(MARGEM, max)))
  return {
    direita: limitar(p.direita, janela.largura - botao.largura - MARGEM),
    baixo: limitar(p.baixo, janela.altura - botao.altura - MARGEM),
  }
}

/** O botão fechado: só o ícone. É por ele que a posição é presa à janela. */
export const BOTAO_FECHADO: Tamanho = { largura: 46, altura: 46 }

/** Com o rótulo "Feedback", que aparece no hover — o botão cresce até aqui. */
export const LARGURA_ABERTO = 124

export type AncoraDoBotao = { right?: number; left?: number; bottom: number; cresce: "esquerda" | "direita" }

/**
 * Para que lado o botão cresce quando o rótulo aparece.
 *
 * O normal é crescer para a esquerda, preso pela direita. Encostado na borda
 * esquerda não há para onde crescer, e o rótulo sairia da tela — então ele passa a
 * ser preso pela esquerda e cresce para a direita. É isso que deixa o botão ir até
 * o extremo esquerdo; antes ele parava a 86 px da borda.
 */
export function ancoraDoBotao(p: Posicao, janela: Tamanho): AncoraDoBotao {
  const esquerda = janela.largura - p.direita - BOTAO_FECHADO.largura
  const cabeACrescerParaEsquerda = esquerda >= LARGURA_ABERTO - BOTAO_FECHADO.largura + MARGEM
  return cabeACrescerParaEsquerda
    ? { right: p.direita, bottom: p.baixo, cresce: "esquerda" }
    : { left: esquerda, bottom: p.baixo, cresce: "direita" }
}

/** Distância do ponto de arrasto: a posição nova quando o ponteiro andou `dx`, `dy`. */
export function mover(inicio: Posicao, dx: number, dy: number): Posicao {
  // Ir para a direita diminui a distância da direita; descer diminui a de baixo.
  return { direita: inicio.direita - dx, baixo: inicio.baixo - dy }
}

export type LugarDoPainel = {
  direita: number
  /** Um dos dois: o painel abre acima do botão (`baixo`) ou abaixo dele (`topo`). */
  baixo?: number
  topo?: number
  alturaMaxima: number
  largura: number
  /** De onde a animação de entrada cresce — o canto colado no botão. */
  origem: string
}

/**
 * Onde o painel abre, dada a posição do botão.
 *
 * Abre acima do botão quando cabe — é o que a pessoa já conhece — e abaixo quando o
 * botão foi levado para o alto da tela. Na horizontal, alinha pela direita do botão
 * e escorrega para dentro da janela se não couber.
 */
export function lugarDoPainel(botaoEm: Posicao, botao: Tamanho, janela: Tamanho, larguraDesejada = 396): LugarDoPainel {
  const largura = Math.min(larguraDesejada, janela.largura - 2 * MARGEM)
  const direita = Math.min(Math.max(botaoEm.direita, MARGEM), Math.max(MARGEM, janela.largura - largura - MARGEM))

  const vao = 12
  const topoDoBotao = janela.altura - botaoEm.baixo - botao.altura
  const espacoAcima = topoDoBotao - vao - MARGEM
  const espacoAbaixo = botaoEm.baixo - vao - MARGEM

  // O ponto da borda do painel que fica em cima (ou embaixo) do centro do botão.
  const centroDoBotao = janela.largura - botaoEm.direita - botao.largura / 2
  const esquerdaDoPainel = janela.largura - direita - largura
  const ladoX = `${Math.round(Math.min(100, Math.max(0, (centroDoBotao - esquerdaDoPainel) / largura * 100)))}%`
  // Acima é o normal; abaixo só quando acima não cabe o painel e abaixo cabe mais.
  if (espacoAcima >= 420 || espacoAcima >= espacoAbaixo) {
    return {
      direita, largura, baixo: botaoEm.baixo + botao.altura + vao,
      alturaMaxima: Math.max(160, espacoAcima), origem: `${ladoX} 100%`,
    }
  }
  return {
    direita, largura, topo: janela.altura - botaoEm.baixo + vao,
    alturaMaxima: Math.max(160, espacoAbaixo), origem: `${ladoX} 0%`,
  }
}
